import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import moment from "moment";
import * as API from "../../store/marketInventory/api";

const StockLevelsDashboard = ({
  stockLevels,
  loading,
  error,
  fetchStockLevels,
  locationId,
  autoRefresh = true,
  isAdmin
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [sortBy, setSortBy] = useState("name");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [batchDetails, setBatchDetails] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState(null);

  // Auto-refresh stock levels every 30 seconds
  useEffect(() => {
    if (locationId) {
      fetchStockLevels(locationId, false);

      if (autoRefresh) {
        const interval = setInterval(() => {
          fetchStockLevels(locationId, false);
        }, 30000);

        return () => clearInterval(interval);
      }
    }
  }, [locationId, fetchStockLevels, autoRefresh]);

  // Filter and sort stock levels
  const getFilteredAndSortedStock = () => {
    if (!stockLevels.levels) return [];

    let filtered = [...stockLevels.levels];

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.item.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== "ALL") {
      filtered = filtered.filter(item => item.status === filterStatus);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.item.localeCompare(b.item);
        case "quantity":
          return b.total_quantity - a.total_quantity;
        case "age":
          return new Date(a.oldest_batch) - new Date(b.oldest_batch);
        case "expiration":
          if (!a.newest_batch && !b.newest_batch) return 0;
          if (!a.newest_batch) return 1;
          if (!b.newest_batch) return -1;
          return new Date(a.newest_batch) - new Date(b.newest_batch);
        default:
          return 0;
      }
    });

    return filtered;
  };

  // Handle View Details click
  const handleViewDetails = async (item) => {
    setSelectedItem(item);
    setShowModal(true);
    setBatchLoading(true);
    setBatchError(null);
    setBatchDetails([]);

    try {
      const response = await API.fetchStockItems(locationId, item.itemId, true);
      const batches = Array.isArray(response.data)
        ? response.data
        : (response.data.results || []);
      setBatchDetails(batches);
    } catch (err) {
      console.error("Error fetching batch details:", err);
      setBatchError(err.response?.data?.message || "Failed to fetch batch details");
    } finally {
      setBatchLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setBatchDetails([]);
    setBatchError(null);
  };

  const filteredStock = getFilteredAndSortedStock();

  return (
    <>
      {/* Summary Stats Row */}
      <div className="row mb-3">
        <div className="col-6 col-md-3">
          <div className="card mb-0">
            <div className="card-body p-3 text-center">
              <h4 className="mb-0">{stockLevels.totalItems || 0}</h4>
              <small className="text-muted">Total Items</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card mb-0">
            <div className="card-body p-3 text-center">
              <h4 className="mb-0" style={{ color: '#fabb3d' }}>{stockLevels.lowStockItems || 0}</h4>
              <small className="text-muted">Low Stock</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card mb-0">
            <div className="card-body p-3 text-center">
              <h4 className="mb-0" style={{ color: '#bdea74' }}>
                {filteredStock.filter(i => i.status === 'NORMAL').length}
              </h4>
              <small className="text-muted">In Stock</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card mb-0">
            <div className="card-body p-3 text-center">
              {stockLevels.lastUpdated ? (
                <>
                  <h6 className="mb-0">{moment(stockLevels.lastUpdated).format('h:mm a')}</h6>
                  <small className="text-muted">Last Updated</small>
                </>
              ) : (
                <small className="text-muted">Loading...</small>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="card">
        {/* Filter Header */}
        <div className="card-header reports-filter">
          <form className="row mb-1">
            <div className="col-12 col-sm-4">
              <label className="col-form-label filter-label" htmlFor="search">
                <i className="fa fa-search mr-05 ml-05"></i>
                Search Item
              </label>
              <input
                type="text"
                id="search"
                className="form-control"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-12 col-sm-4">
              <label className="col-form-label filter-label ml-05" htmlFor="status">
                Status
              </label>
              <select
                className="form-control"
                id="status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="ALL">All Status</option>
                <option value="NORMAL">Normal Stock</option>
                <option value="LOW_STOCK">Low Stock</option>
                <option value="OUT_OF_STOCK">Out of Stock</option>
              </select>
            </div>
            <div className="col-12 col-sm-4">
              <label className="col-form-label filter-label ml-05" htmlFor="sort">
                Sort By
              </label>
              <select
                className="form-control"
                id="sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="name">Item Name</option>
                <option value="quantity">Quantity</option>
                <option value="age">Oldest Stock</option>
                <option value="expiration">Expiration</option>
              </select>
            </div>
          </form>
        </div>

        {/* Error Display */}
        {error && (
          <div className="alert alert-danger m-3" role="alert">
            {error}
          </div>
        )}

        {/* Stock Table */}
        {loading && filteredStock.length === 0 ? (
          <div className="card-body text-center py-5">
            <i className="fa fa-spinner fa-spin fa-2x"></i>
            <p className="mt-2 text-muted">Loading stock levels...</p>
          </div>
        ) : filteredStock.length === 0 ? (
          <div className="shipment-list--empty">
            <div className="fa fa-cubes icon" aria-hidden="true"></div>
            <div>No stock items found</div>
          </div>
        ) : (
          <table className="table table-striped table-bordered datatable mb-0">
            <thead>
              <tr>
                <th>Item Name</th>
                <th className="text-center">Quantity</th>
                <th className="text-center hidden-xs-down">Batches</th>
                <th className="hidden-xs-down">Oldest Batch</th>
                <th className="hidden-xs-down">Newest Batch</th>
                <th className="text-center">Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStock.map((item, index) => (
                <tr key={index} className={item.status === 'OUT_OF_STOCK' ? 'error-row' : ''}>
                  <td>
                    <strong>{item.item}</strong>
                  </td>
                  <td className="text-center">
                    <span>{item.total_quantity}</span>
                    <span className="badge badge-pill badge-info ml-05">{item.unit}</span>
                  </td>
                  <td className="text-center hidden-xs-down">
                    <span className="badge badge-pill badge-secondary">{item.batches}</span>
                  </td>
                  <td className="hidden-xs-down">
                    {item.oldest_batch ? moment(item.oldest_batch).format('MM/DD/YY') : '-'}
                  </td>
                  <td className="hidden-xs-down">
                    {item.newest_batch ? moment(item.newest_batch).format('MM/DD/YY') : '-'}
                  </td>
                  <td className="text-center">
                    <span className={`badge badge-pill ${
                      item.status === 'NORMAL' ? 'badge-success' :
                      item.status === 'LOW_STOCK' ? 'badge-warning' : 'badge-danger'
                    }`}>
                      {item.status === 'NORMAL' ? 'In Stock' :
                       item.status === 'LOW_STOCK' ? 'Low Stock' : 'Out of Stock'}
                    </span>
                  </td>
                  <td className="text-center">
                    <button
                      className="btn-sm btn-info"
                      onClick={() => handleViewDetails(item)}
                      title="View batch details"
                    >
                      <i className="fa fa-eye"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Refresh indicator */}
        {loading && filteredStock.length > 0 && (
          <div className="card-footer text-center py-2">
            <small className="text-muted">
              <i className="fa fa-refresh fa-spin mr-05"></i> Refreshing...
            </small>
          </div>
        )}
      </div>

      {/* Batch Details Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" dialogClassName="modal-logut" animation={false}>
        <div className="card mb-0">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span>
              <i className="fa fa-boxes mr-05"></i>
              {selectedItem?.item} - Batch Details
            </span>
            <button type="button" className="btn-close" onClick={handleCloseModal}></button>
          </div>

          <div className="card-body p-0">
            {batchLoading ? (
              <div className="text-center py-5">
                <i className="fa fa-spinner fa-spin fa-2x"></i>
                <p className="mt-2 text-muted">Loading batch details...</p>
              </div>
            ) : batchError ? (
              <div className="alert alert-danger m-3">{batchError}</div>
            ) : batchDetails.length === 0 ? (
              <div className="shipment-list--empty">
                <div className="fa fa-inbox icon" aria-hidden="true"></div>
                <div>No batch details found</div>
              </div>
            ) : (
              <>
                <table className="table table-striped table-bordered datatable mb-0">
                  <thead>
                    <tr>
                      <th>Inventory IDs</th>
                      <th className="text-center">Received</th>
                      <th className="text-center">Expires</th>
                      <th className="text-center">Initial</th>
                      <th className="text-center">Current</th>
                      <th className="text-center">Remaining</th>
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchDetails.map((batch) => (
                      <tr key={batch.id}>
                        <td>
                          <code style={{ fontSize: '11px' }}>
                            {batch.inventory_ids?.length > 0
                              ? batch.inventory_ids.join(', ')
                              : '-'}
                          </code>
                        </td>
                        <td className="text-center">
                          {moment(batch.received_date).format('MM/DD/YY')}
                        </td>
                        <td className="text-center">
                          {batch.expiration_date ? (
                            <span className={
                              batch.days_until_expiration <= 3 ? 'text-danger fw-bold' :
                              batch.days_until_expiration <= 7 ? 'text-warning' : ''
                            }>
                              {moment(batch.expiration_date).format('MM/DD/YY')}
                              <small className="d-block text-muted">
                                {batch.days_until_expiration}d left
                              </small>
                            </span>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </td>
                        <td className="text-center">{batch.initial_quantity}</td>
                        <td className="text-center">
                          <strong>{batch.current_quantity}</strong>
                        </td>
                        <td className="text-center">
                          <div className="progress" style={{ height: '18px', minWidth: '80px' }}>
                            <div
                              className={`progress-bar ${
                                batch.stock_percentage > 50 ? 'bg-success' :
                                batch.stock_percentage > 20 ? 'bg-warning' : 'bg-danger'
                              }`}
                              style={{ width: `${batch.stock_percentage || 0}%` }}
                            >
                              {batch.stock_percentage?.toFixed(0) || 0}%
                            </div>
                          </div>
                        </td>
                        <td className="text-center">
                          <span className={`badge badge-pill ${
                            batch.is_active ? 'badge-success' :
                            batch.fully_consumed ? 'badge-secondary' : 'badge-danger'
                          }`}>
                            {batch.is_active ? 'Active' :
                             batch.fully_consumed ? 'Consumed' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Summary Footer */}
                <div className="card-footer" style={{ backgroundColor: '#f9f9f9' }}>
                  <div className="row text-center">
                    <div className="col-4">
                      <strong>{batchDetails.length}</strong>
                      <br />
                      <small className="text-muted">Total Batches</small>
                    </div>
                    <div className="col-4">
                      <strong>{batchDetails.reduce((sum, b) => sum + (b.current_quantity || 0), 0)}</strong>
                      <br />
                      <small className="text-muted">Total Qty</small>
                    </div>
                    <div className="col-4">
                      <strong style={{ color: batch => batch > 0 ? '#ff5454' : 'inherit' }}>
                        {batchDetails.filter(b => b.days_until_expiration !== null && b.days_until_expiration <= 7).length}
                      </strong>
                      <br />
                      <small className="text-muted">Expiring (7d)</small>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="card-footer text-end">
            <button className="btn btn-secondary" onClick={handleCloseModal}>
              Close
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default StockLevelsDashboard;
